// optimizacionPreview.js — P3-9b (preview). Descarga una media por el PROXY del
// backend (same-origin → el canvas no se «tiñe» como con una URL cross-origin de
// HostGator), la reprocesa EN MEMORIA con la tubería de P3-1 y mide el peso nuevo
// REAL. NO escribe nada (ni sube ni reasigna) — es el preview que decide, con
// números de verdad, qué conviene optimizar. El criterio y las sumas son puros
// (criterioOptimizacion.js / fotosOptimizar.js), acá va solo el trabajo de navegador.
import { procesarImagenParaSubir } from './productImages';
import { decidirReproceso } from './criterioOptimizacion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function bytesDeDataUrl(d) {
  const i = String(d).indexOf(',');
  return i < 0 ? 0 : Math.round((d.length - i - 1) * 3 / 4);
}
function blobADataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error('no se pudo leer la imagen'));
    r.readAsDataURL(blob);
  });
}
function dimensiones(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('imagen ilegible'));
    img.src = dataUrl;
  });
}

// Mide UNA media (por su id de mediateca). → { ...media, ok, ancho, alto,
// pesoOriginal, pesoNuevo, reprocesar, motivo, ahorroPct, ahorroBytes } | { ok:false, error }.
// P3-12: `prop` = { maxW, maxH } de la proporción del TIPO (default 4:5 1600×2000). El techo de la
// tubería y el criterio «sobredimensionada» salen de ahí (un cosmético 1:1 se mide contra 1600×1600).
export async function medirMedia(media, { fetchFn, prop } = {}) {
  const f = fetchFn || ((...a) => fetch(...a));
  const maxW = (prop && prop.maxW) || 1600;
  const maxH = (prop && prop.maxH) || 2000;
  try {
    if (media.id == null) throw new Error('la foto no tiene id en la mediateca');
    const res = await f(`${API_URL}/api/catalog/media/${media.id}/archivo`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const pesoOriginal = blob.size;
    const dataUrl = await blobADataUrl(blob);
    const { width, height } = await dimensiones(dataUrl);
    const nuevaDataUrl = await procesarImagenParaSubir(dataUrl, { maxW, maxH });
    const pesoNuevo = bytesDeDataUrl(nuevaDataUrl);
    const decision = decidirReproceso({ ancho: width, alto: height, pesoOriginal, pesoNuevo, techoW: maxW, techoH: maxH });
    return { ...media, ok: true, ancho: width, alto: height, pesoOriginal, pesoNuevo, ...decision };
  } catch (e) {
    return { ...media, ok: false, error: e.message };
  }
}

// P3-9b (escritura): reprocesa UNA media para SUBIRLA — descarga por el proxy,
// pasa por la tubería y devuelve el JPG nuevo como base64 (+ peso). NO sube; eso lo
// hace el backend (/api/catalog/optimizar-foto) con el base64 que esto produce.
export async function reprocesarParaSubir(media, { fetchFn, prop } = {}) {
  const f = fetchFn || ((...a) => fetch(...a));
  if (media.id == null) throw new Error('la foto no tiene id en la mediateca');
  const res = await f(`${API_URL}/api/catalog/media/${media.id}/archivo`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const pesoOriginal = blob.size;
  const dataUrl = await blobADataUrl(blob);
  const nuevaDataUrl = await procesarImagenParaSubir(dataUrl, { maxW: (prop && prop.maxW) || 1600, maxH: (prop && prop.maxH) || 2000 });
  const coma = nuevaDataUrl.indexOf(',');
  const base64 = coma < 0 ? '' : nuevaDataUrl.slice(coma + 1);
  return { base64, mime: 'image/jpeg', pesoOriginal, pesoNuevo: bytesDeDataUrl(nuevaDataUrl) };
}

// Mide una lista EN SERIE (gentil con el hosting) reportando progreso. NO escribe.
export async function medirLista(medias, { onProgreso, fetchFn, prop } = {}) {
  const out = [];
  for (let i = 0; i < medias.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const r = await medirMedia(medias[i], { fetchFn, prop });
    out.push(r);
    if (onProgreso) onProgreso(i + 1, medias.length, r);
  }
  return out;
}
